import { CalendarDays, MapPin, UsersRound } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { NOME_ENQUADRAMENTO, NOME_TIPO_SOCIETARIO } from '@social/shared';
import { Etiqueta } from '../componentes/ui';
import {
  classeSeloTipo,
  cnpjBR,
  dataBR,
  moeda,
  semRegistro,
  sociosAtuais,
  type SociedadeResumo,
} from './sociedade-comum';

/**
 * O card da sociedade.
 *
 * Segue a mesma divisão em andares do card de pedido do Adonai — identificação,
 * de quem é, fecho — traduzida para o vocabulário societário:
 *
 * 1. **Identificação** — o nome empresarial é a manchete, porque ninguém
 *    procura "a sociedade x123": procura "a Alfa Comércio". Tipo e
 *    enquadramento são etiquetas ao lado, não texto corrido.
 * 2. **De quem é** — início de atividades, município/UF e quantos sócios tem
 *    hoje. O selo de "sem registro" mora aqui, quando for o caso — é o dado
 *    que mais importa para quem está de olho no atraso da Junta.
 * 3. **Fecho** — CNPJ à esquerda (ou o travessão, se ainda não existe),
 *    capital social à direita: o número que mais se pergunta sobre uma
 *    sociedade.
 */
export function CartaoSociedade({
  sociedade: s,
  acoes,
}: {
  sociedade: SociedadeResumo;
  acoes: ReactNode;
}) {
  return (
    <article className="border-tinta-100 bg-superficie hover:border-marca-400 hover:bg-marca-600/[0.04] flex h-full flex-col gap-2 rounded-xl border p-3 transition-all hover:shadow-md sm:gap-3 sm:p-4 sm:hover:-translate-y-0.5">
      <header className="flex items-start gap-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <Link
            to={`/sociedades/${s.id}`}
            className="text-tinta-900 truncate font-semibold hover:underline"
          >
            {s.nomeEmpresarial}
          </Link>
        </div>
        {acoes && <div className="-mt-1 -mr-1 shrink-0">{acoes}</div>}
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <span className={classeSeloTipo(s.tipo)} title={NOME_TIPO_SOCIETARIO[s.tipo]}>
          {s.tipo}
        </span>
        <Etiqueta>{NOME_ENQUADRAMENTO[s.enquadramento]}</Etiqueta>
        {semRegistro(s) && <Etiqueta tom="erro">Sem registro na Junta</Etiqueta>}
      </div>

      <dl className="text-tinta-500 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:gap-x-4">
        <div className="flex items-center gap-1.5">
          <CalendarDays className="size-3.5 shrink-0" aria-hidden />
          <dt className="sr-only">Início de atividades</dt>
          <dd className="tabular-nums">{dataBR(s.inicioAtividades)}</dd>
        </div>
        <div className="flex items-center gap-1.5">
          <MapPin className="size-3.5 shrink-0" aria-hidden />
          <dt className="sr-only">Município</dt>
          <dd>
            {s.sede.municipio}/{s.sede.uf}
          </dd>
        </div>
        <div className="flex items-center gap-1.5">
          <UsersRound className="size-3.5 shrink-0" aria-hidden />
          <dt className="sr-only">Sócios</dt>
          <dd className="tabular-nums">
            {sociosAtuais(s)} {sociosAtuais(s) === 1 ? 'sócio' : 'sócios'}
          </dd>
        </div>
      </dl>

      <footer className="border-tinta-100 mt-auto flex items-end justify-between gap-3 border-t pt-2 sm:pt-3">
        <p className="text-tinta-500 truncate font-mono text-xs" title={cnpjBR(s.cnpj)}>
          {cnpjBR(s.cnpj)}
        </p>
        <p className="text-tinta-900 text-lg font-semibold tabular-nums">
          {moeda(s.capitalCentavos)}
        </p>
      </footer>
    </article>
  );
}
